/**
 * `useSelectionCapture` — App-shell-level hook that watches the
 * browser's text-selection state and pipes selections originating in
 * a `[data-selectable-source]` ancestor into `useSelectionContextStore`.
 *
 * Mounted ONCE at the App shell so the feature is universal across
 * routes (case-chat, /chat global-org-scope, research, anywhere a
 * viewer renders selectable text). Per-viewer wiring is just the
 * `data-selectable-source={file.path}` attribute on the viewer's
 * scrollable content container.
 *
 * **Listener: `selectionchange` + rAF debouncing.** The chip mirrors
 * the browser's selection live (operator brief 2026-06-28): a new
 * drag-select replaces the previous chip the moment the browser's
 * selection updates; clicking elsewhere to collapse the selection
 * clears the chip the moment the browser collapses. `mouseup`-only
 * listening was insufficient — between mousedown (collapses old
 * selection visually) and mouseup (commits new range), the chip
 * would briefly mismatch what the user saw highlighted.
 *
 * `selectionchange` fires on EVERY caret tick (shift-arrows in a
 * textarea, native double-click word expansion, etc.). To avoid
 * React churn we debounce to one update per animation frame —
 * 60Hz is plenty for "chip mirrors selection".
 *
 * **No capture if a typing target has focus.** When the user is in
 * the composer textarea (or any input), `window.getSelection()` may
 * report the textarea's own selection — which is not a viewer
 * reference. We skip capture when the active element is editable.
 *
 * **Selection NOT in a `[data-selectable-source]` ancestor.** The
 * user might select text in chrome (nav labels, the breadcrumbs,
 * tooltips). We leave the existing chip ALONE in that case — the
 * lawyer's previous viewer selection is still valid and shouldn't
 * be wiped by an accidental nav-text drag.
 */

import { useEffect } from "react";
import { useSelectionContextStore } from "@/stores/useSelectionContextStore";

const SELECTABLE_SOURCE_ATTR = "data-selectable-source";

export function useSelectionCapture() {
  const capture = useSelectionContextStore((s) => s.capture);
  const clear = useSelectionContextStore((s) => s.clear);

  useEffect(() => {
    let raf = 0;

    function schedule() {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        run();
      });
    }

    function run() {
      // Skip when the user is typing in the composer (or any input /
      // contenteditable) — `window.getSelection()` would report the
      // textarea's own caret, not a viewer reference.
      const active = document.activeElement;
      if (isEditableElement(active)) return;

      const sel = window.getSelection();
      const text = sel?.toString() ?? "";

      // Empty / collapsed → user has nothing selected; clear the
      // chip. This is the "deselect → chip vanishes" branch.
      if (!sel || sel.isCollapsed || !text.trim()) {
        clear();
        return;
      }

      // Find the nearest selectable-source ancestor from the selection's
      // anchor node. If absent, the selection is in chrome / non-viewer
      // surface — preserve whatever's already in the store (operator
      // intent: an accidental nav-text drag shouldn't wipe a real
      // viewer selection).
      const source = findSelectableSource(sel.anchorNode);
      if (!source) return;

      // Pass the live Range so `useSelectionHighlight` can paint a
      // persistent CSS Custom Highlight that survives the user
      // clicking into the composer textarea (which would otherwise
      // collapse the browser's native ::selection visual).
      const range = sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
      capture(text, source, range);
    }

    document.addEventListener("selectionchange", schedule);
    return () => {
      document.removeEventListener("selectionchange", schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [capture, clear]);
}

/** Minimal element shape `findSelectableSourceFromElement` walks.
 *  Real DOM `Element`s satisfy this trivially; POJO mocks in the unit
 *  test do too — keeps the helper testable WITHOUT a DOM env, per the
 *  lexide vitest convention (`vitest.config.ts` — environment: "node").
 *  Exported so the test can type-check its mocks. */
export interface SelectableElementLike {
  getAttribute(name: string): string | null;
  readonly parentElement: SelectableElementLike | null;
}

/** Pure element-only walk — finds the nearest ancestor (including
 *  `el` itself) carrying `data-selectable-source` and returns its
 *  value. The Node→Element conversion is `findSelectableSource`'s job. */
export function findSelectableSourceFromElement(
  el: SelectableElementLike | null,
): string | null {
  let cur: SelectableElementLike | null = el;
  while (cur) {
    const v = cur.getAttribute(SELECTABLE_SOURCE_ATTR);
    if (v) return v;
    cur = cur.parentElement;
  }
  return null;
}

/** Walk up from a DOM `Node` (possibly a `Text` node, which is what
 *  selection anchors usually are) to find the nearest selectable-source
 *  ancestor. Thin Node→Element wrapper over the pure walker above. */
export function findSelectableSource(node: Node | null): string | null {
  const el =
    node && node.nodeType === 1 /* Node.ELEMENT_NODE */
      ? (node as unknown as SelectableElementLike)
      : (node?.parentElement as unknown as SelectableElementLike | null) ?? null;
  return findSelectableSourceFromElement(el);
}

/** Minimal editable-shape used by `isEditableElement` — TEXTAREA /
 *  INPUT detected via `tagName`; rich-text editors detected via
 *  `isContentEditable`. POJO-friendly for unit tests. */
export interface EditableElementLike {
  readonly tagName: string;
  readonly isContentEditable?: boolean;
}

/** True when the element is a typing target (textarea / input / any
 *  contenteditable). Selection inside these is the user editing
 *  their own message — not a viewer reference to embed. */
export function isEditableElement(el: EditableElementLike | null): boolean {
  if (!el) return false;
  if (el.tagName === "TEXTAREA" || el.tagName === "INPUT") return true;
  if (el.isContentEditable) return true;
  return false;
}
